import { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  Participation,
  OrderStatus,
  useIsAdmin,
  useSessionOrders,
  ParticipationStatus,
  useUpdateParticipationIncentive,
  Incentive,
} from '@app/api';
import { EditableOrder } from '../Admin/EditableOrder';
import { AdminIncentiveEdition } from '../Admin/AdminIncentiveEdition';
import {
  useBoolModalState,
  SquareButton,
  InformationBox,
  useToast,
  useToastOptions,
} from '@app/core';
import { IncentiveBlock } from 'components';
import { IncentivePaymentBlock } from '../Admin/IncentivePaymentBlock';

const useStyles = createUseStyles((theme: Theme) => ({
  wrapper: {
    padding: theme.marginBase * 4,
    overflow: 'auto',
    height: '100%',
  },
  orders: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase,
  },
  incentiveDiplayed: {
    ...theme.fonts.caption,
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    padding: [[0, theme.marginBase * 2]],
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase * 2,
    minHeight: theme.height.small,
    display: 'flex',
    alignItems: 'center',
  },
  incentiveBlock: {
    paddingTop: 0,
    paddingBottom: 0,
    flex: 1,
    minHeight: theme.height.small,
    display: 'flex',
    alignItems: 'center',
  },
  incentiveLine: {
    display: 'flex',
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 4,
    gap: theme.marginBase * 2,
    alignItems: 'center',
  },
  incentivePaymentContainer: {
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 4,
  },
  incentivePayment: {
    ...theme.fonts.caption,
    display: 'flex',
    gap: theme.marginBase * 2,
    alignItems: 'center',
  },
  incentivePaymentCell: {},
  spacer: {
    flex: 1,
  },
  title: {
    ...theme.fonts.h4,
  },
}));

interface Props {
  participation: Participation;
}

const orderStatus: OrderStatus[] = [
  OrderStatus.CANCELLED,
  OrderStatus.PENDING,
  OrderStatus.COMPLETED,
  OrderStatus.BILLING_IN_PROGRESS,
  OrderStatus.BILLED,
];

export const ParticipationBillingTab = ({ participation }: Props) => {
  const classes = useStyles();

  const isAdmin = useIsAdmin();
  const { data: allOrders } = useSessionOrders(
    participation?.sessionId,
    orderStatus,
  );
  const [editingIncentive, editIncentive, stopEditingIncentive] =
    useBoolModalState();

  const { mutate: updateIncentive } = useUpdateParticipationIncentive(
    participation.id,
  );

  const orders = useMemo(() => {
    if (!participation || !allOrders) {
      return undefined;
    }
    const founds = allOrders.filter(
      (o) => o.participationId === participation.id,
    );
    founds.sort(
      (a, b) =>
        orderStatus.findIndex((s) => s === b.status) -
        orderStatus.findIndex((s) => s === a.status),
    );
    return founds;
  }, [allOrders, participation]);

  const toastOptions = useToastOptions({
    onSuccess: () => stopEditingIncentive(),
  });

  if (!isAdmin) {
    return null;
  }

  return (
    <div className={classes.wrapper}>
      {participation.status !== ParticipationStatus.INVITED && (
        <>
          <h2 className={classes.title}>Contrepartie prévue</h2>
          {editingIncentive ? (
            <AdminIncentiveEdition
              incentive={participation.incentive}
              onSubmit={(incentive: Incentive) =>
                updateIncentive(incentive, toastOptions)
              }
            />
          ) : participation.incentive ? (
            <div className={classes.incentiveLine}>
              <IncentiveBlock
                incentive={participation.incentive}
                className={classes.incentiveBlock}
              />
              <SquareButton
                color="green"
                icon="ri-edit-line"
                onClick={editIncentive}
              />
            </div>
          ) : (
            <div className={classes.incentiveLine}>
              <InformationBox>
                Contrepartie non fixée pour l'instant
              </InformationBox>
            </div>
          )}

          <h2 className={classes.title}>Incentive Payment</h2>
          <div className={classes.incentivePaymentContainer}>
            <IncentivePaymentBlock participation={participation} />
          </div>
        </>
      )}
      <h2 className={classes.title}>Orders</h2>
      <div className={classes.orders}>
        {orders?.map((order) => (
          <EditableOrder order={order} />
        ))}
      </div>
    </div>
  );
};
